import React, { useState, useEffect, useCallback } from 'react';
import { Container, Paper, Title, Text, Stack as MantineStack, Button, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Game, Player, Stack, StackEntry } from '../types/models';
import { apiClient } from '../services/api';
import { GameLayout } from './layouts/GameLayout';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useDraftPersistence } from '../hooks/useDraftPersistence';

interface InitialPromptProps {
  game: Game;
  currentPlayer: Player;
  stack: Stack;
  onPromptSubmitted: (updatedStack: Stack) => void;
  onReturnToLobby?: () => void;
}

const InitialPrompt: React.FC<InitialPromptProps> = ({ game, currentPlayer, stack, onPromptSubmitted, onReturnToLobby }) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [othersWritingCount, setOthersWritingCount] = useState(0);
  const { loadDraft, saveDraft, clearDraft } = useDraftPersistence({
    gameKey: game.key,
    stackId: stack.id,
    entryType: 'prompt'
  });
  
  // Get user's handwriting font
  const handwritingFont = localStorage.getItem('userHandwriting') || 'Caveat';

  useEffect(() => {
    let isActive = true;

    const restoreDraft = async () => {
      const saved = await loadDraft();
      if (!isActive || !saved) return;
      setPrompt(saved);
    };

    restoreDraft();

    return () => {
      isActive = false;
    };
  }, [loadDraft]);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (isLoading) return;
      if (prompt.trim()) {
        saveDraft(prompt);
      } else {
        clearDraft();
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [clearDraft, isLoading, prompt, saveDraft]);

  // Add navigation guard to prevent accidental back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (prompt.trim() && !isLoading) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [prompt, isLoading]);

  // Count how many other players are also writing their initial prompts
  useEffect(() => {
    const otherStacks = game.stacks.filter(s => 
      s.originPlayerId !== currentPlayer.id && 
      s.entries.length === 0 && // No entries yet (still waiting for initial prompt)
      s.expectedEntries.length > 0 &&
      !s.expectedEntries[0].completed // First expected entry not completed
    );
    setOthersWritingCount(otherStacks.length);
  }, [game.stacks, currentPlayer.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) {
      setError('Prompt cannot be empty.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create the initial prompt entry
      const response = await apiClient.createStackEntry(game.key, stack.id, {
        content: prompt.trim(),
        type: 'text'
      });
      
      console.log('Initial prompt created successfully');
      await clearDraft();
      setPrompt('');
      onPromptSubmitted(response.data);

    } catch (err: any) {
      setError(err.error || 'Failed to submit prompt.');
      notifications.show({
        title: 'Failed to submit prompt',
        message: 'Unable to save your prompt. Please try again.',
        color: 'red',
      });
      console.error('Prompt submission error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReturnToLobby = useCallback(async () => {
    if (!onReturnToLobby || isLoading) return;
    if (prompt.trim()) {
      await saveDraft(prompt);
    } else {
      await clearDraft();
    }
    onReturnToLobby();
  }, [clearDraft, isLoading, onReturnToLobby, prompt, saveDraft]);

  const showReturnButton = Boolean(onReturnToLobby);

  return (
    <Container size="md" py="xl">
      <Paper p="xl" radius="md" shadow="sm">
        <MantineStack gap="xl" align="center">
          {/* Header */}
          <MantineStack gap="sm" align="center">
            <Title order={2} ta="center">Write a starting prompt!</Title>
            <Text size="md" c="dimmed" ta="center">
              Write something for the next player to draw
            </Text>
            <Text size="sm" c="dimmed" ta="center" style={{ fontStyle: 'italic' }}>
              Be nice—some poor sap will have to draw this!
            </Text>
          </MantineStack>

          {/* Social Awareness */}
          {othersWritingCount > 0 && (
            <Text size="sm" c="blue" ta="center">
              {othersWritingCount} {othersWritingCount === 1 ? 'other is' : 'others are'} writing their prompts too...
            </Text>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '500px' }}>
            <MantineStack gap="md">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.currentTarget.value)}
                placeholder="e.g., A cat riding a unicorn on the moon"
                rows={4}
                required
                size="lg"
                autosize
                minRows={3}
                maxRows={6}
                styles={{
                  input: {
                    fontFamily: `"${handwritingFont}", var(--mantine-font-family)`,
                    fontSize: '20px',
                  }
                }}
              />

              {error && (
                <Text c="red" size="sm" ta="center">
                  {error}
                </Text>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={isLoading || !prompt.trim()}
                loading={isLoading}
                style={{ backgroundColor: '#0da388', color: 'white' }}
              >
                {isLoading ? 'Passing your stack...' : 'Pass it!'}
              </Button>
              {showReturnButton && (
                <Button
                  type="button"
                  size="lg"
                  variant="light"
                  disabled={isLoading}
                  onClick={handleReturnToLobby}
                >
                  Save & lobby
                </Button>
              )}
            </MantineStack>
          </form>
        </MantineStack>
      </Paper>
    </Container>
  );
};

export default InitialPrompt; 
